import { InfoNotice } from '@/components/patterns/info-notice'
import { DisabledSurfaceOverlay } from '@/components/patterns/disabled-surface-overlay'
import {
  ScheduleAutomationFields,
  clearScheduleFixedEndDate,
  handleFixedEndDateScheduleChange,
  handleStartHolidayScheduleChange,
  switchScheduleToFixedDate,
  switchScheduleToHoliday,
} from '@/components/forms/ScheduleAutomationFields'
import { Typography } from '@/components/ui/typography'
import { ReadOnly } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { useFormContext } from '@/lib/form-contexts'
import { AlertCircleIcon } from 'lucide-react'
import { useId } from 'react'

const PREFIX = 'schedule'

const ScheduleForm = ({ hideEnabledToggle = false }) => {
  const form = useFormContext()
  const id = useId()
  const scheduleMinDate = dayjs().startOf('day')
  const scheduleMaxDate = dayjs().add(1, 'year').endOf('year')
  const todayDate = dayjs().format('YYYY-MM-DD')
  const handleScheduleFieldChange = (listener) => (payload) => {
    listener?.(payload)
  }
  const switchToFixedDateSchedule = () =>
    switchScheduleToFixedDate({ form, fieldPrefix: PREFIX, todayDate })
  const switchToHolidaySchedule = () => switchScheduleToHoliday({ form, fieldPrefix: PREFIX })
  const handleStartHolidayChange = (value) =>
    handleStartHolidayScheduleChange({ form, fieldPrefix: PREFIX, value })
  const handleFixedEndDateChange = (value) =>
    handleFixedEndDateScheduleChange({ form, fieldPrefix: PREFIX, value })
  const clearFixedEndDate = () => clearScheduleFixedEndDate({ form, fieldPrefix: PREFIX })

  return (
    <div className="flex flex-col gap-6">
      {!hideEnabledToggle ? (
        <form.AppField name="schedule.is_enabled">
          {(field) => (
            <field.SwitchInput
              label="Enable Schedule"
              description="When enabled, this design can automatically activate during its date window."
            />
          )}
        </form.AppField>
      ) : null}

      <form.Subscribe selector={(state) => state.values?.schedule?.is_enabled}>
        {(isEnabled) => (
          <div className="mt-5">
            <div className="relative rounded-lg border border-border/70 bg-muted/20 p-4">
              {!isEnabled && <DisabledSurfaceOverlay label="Schedule is disabled" />}

              <div className={isEnabled ? '' : 'opacity-55 saturate-0'}>
                <ReadOnly isReadOnly={!isEnabled}>
                  <div className="space-y-6">
                    {isEnabled && (
                      <InfoNotice
                        title="Schedule automation is on"
                        icon={<AlertCircleIcon />}
                        compact
                      >
                        During this active window, this design will automatically become active and
                        will override manual activation. Turn off the schedule first if you want
                        manual control.
                      </InfoNotice>
                    )}

                    <div className="space-y-4">
                      <Typography variant="h4">Schedule timing</Typography>
                      <ScheduleAutomationFields
                        clearFixedEndDate={clearFixedEndDate}
                        form={form}
                        handleFixedEndDateChange={handleFixedEndDateChange}
                        handleScheduleFieldChange={handleScheduleFieldChange}
                        handleStartHolidayChange={handleStartHolidayChange}
                        id={id}
                        scheduleMaxDate={scheduleMaxDate}
                        scheduleMinDate={scheduleMinDate}
                        scheduleTypeLabel="Choose a holiday-based schedule or a specific date range."
                        switchToFixedDateSchedule={switchToFixedDateSchedule}
                        switchToHolidaySchedule={switchToHolidaySchedule}
                      />
                    </div>
                  </div>
                </ReadOnly>
              </div>
            </div>
          </div>
        )}
      </form.Subscribe>
    </div>
  )
}

export default ScheduleForm
